/**
 * VerifyDiagnoseCard — admin tool to debug + unblock email-verification.
 *
 * Built for the 2026-06-17 production verify-email blocker. Two flows:
 *
 *   1. "Trace" — given an email, calls /api/admin/verify-token-trace and
 *      shows the full DB state for that user's verification tokens, plus
 *      a plain-English interpretation. This is what answers "is the token
 *      in the database?" without needing CLI access.
 *
 *   2. "Manually verify" — flips email_verified=true for the given user,
 *      bypassing the broken token round-trip. Audited. Use this to
 *      unblock individual beta testers while we close the root cause.
 *
 * Read-only for the Trace flow. Manual verify requires admin role
 * (enforced server-side) and a free-text reason for the audit log.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ShieldCheck, Search, AlertCircle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const VerifyDiagnoseCard = () => {
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [trace, setTrace] = useState(null);
  const [loadingTrace, setLoadingTrace] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const runTrace = async () => {
    if (!email.trim()) {
      toast.error('Enter an email first');
      return;
    }
    setLoadingTrace(true);
    setTrace(null);
    try {
      const res = await axios.get(`${API}/admin/verify-token-trace`, {
        params: { email: email.trim() },
        withCredentials: true,
      });
      setTrace(res.data);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Trace failed';
      toast.error(detail);
    } finally {
      setLoadingTrace(false);
    }
  };

  const runManualVerify = async () => {
    if (!email.trim()) {
      toast.error('Enter an email first');
      return;
    }
    if (!reason.trim()) {
      toast.error('Audit reason is required');
      return;
    }
    if (!window.confirm(`Manually verify ${email.trim()}? This will be logged in the admin audit log.`)) {
      return;
    }
    setVerifying(true);
    try {
      const res = await axios.post(
        `${API}/admin/manually-verify-user`,
        { email: email.trim(), reason: reason.trim() },
        { withCredentials: true },
      );
      const wasAlready = res.data?.was_already_verified;
      toast.success(wasAlready ? 'User was already verified — no change.' : 'User is now verified.');
      // Refresh the trace so the admin can see the new state.
      await runTrace();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Manual verify failed';
      toast.error(detail);
    } finally {
      setVerifying(false);
    }
  };

  const interpretation = trace?.interpretation;
  const tokens = trace?.tokens || [];
  const userBlock = trace?.user;

  return (
    <Card
      className="border border-amber-200 bg-amber-50/30 rounded-2xl shadow-sm"
      data-testid="admin-verify-diagnose-card"
    >
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-amber-900">
          <ShieldCheck className="h-5 w-5" />
          Email-Verification Diagnose &amp; Unblock
        </CardTitle>
        <CardDescription className="text-amber-800">
          Inspect a user&apos;s verification token state directly from the
          production DB, or manually flip them to verified if they&apos;re
          stuck behind the broken token flow.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            data-testid="admin-verify-diagnose-email"
            type="email"
            placeholder="user@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="bg-white"
          />
          <Button
            data-testid="admin-verify-diagnose-trace-btn"
            onClick={runTrace}
            disabled={loadingTrace}
            variant="outline"
          >
            <Search className="h-4 w-4 mr-1" />
            {loadingTrace ? 'Tracing…' : 'Trace tokens'}
          </Button>
        </div>

        {trace && (
          <div className="rounded-md border border-amber-200 bg-white p-3 text-sm space-y-2">
            <div className="font-medium text-gray-900">
              {trace.user_found
                ? `User found: ${userBlock?.email}`
                : `No user with that email in this DB.`}
            </div>
            {trace.user_found && userBlock && (
              <div className="text-xs text-gray-600 font-mono space-y-0.5">
                <div>user.id: <span className="text-gray-900">{userBlock.id}</span></div>
                <div>email_verified: <span className={userBlock.email_verified ? 'text-emerald-700 font-semibold' : 'text-rose-700 font-semibold'}>{String(Boolean(userBlock.email_verified))}</span></div>
                {userBlock.email_verified_at && (
                  <div>email_verified_at: <span className="text-gray-900">{userBlock.email_verified_at}</span></div>
                )}
                {userBlock.auth_provider && (
                  <div>auth_provider: <span className="text-gray-900">{userBlock.auth_provider}</span></div>
                )}
              </div>
            )}
            <div className="pt-1 border-t border-amber-100">
              <div className="text-xs font-medium text-gray-700 mb-1">
                Token rows ({trace.token_count}):
              </div>
              {tokens.length === 0 ? (
                <div className="text-xs text-rose-700">
                  Zero token rows for this user. Registration never enqueued a verify-email task, or the background task crashed before insert.
                </div>
              ) : (
                <ul className="space-y-1 text-xs font-mono">
                  {tokens.map((t, i) => (
                    <li
                      key={i}
                      className={`rounded px-2 py-1 ${t.used ? 'bg-gray-50 text-gray-700' : 'bg-emerald-50 text-emerald-800'}`}
                    >
                      hash_prefix=<b>{t.token_hash_prefix}</b> used={String(Boolean(t.used))} created={t.created_at} expires={t.expires_at}
                      {t.invalidated_at ? ` invalidated_at=${t.invalidated_at}` : ''}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {interpretation && (
              <div className="text-xs text-amber-900 bg-amber-100/60 border border-amber-200 rounded p-2 mt-2">
                <AlertCircle className="inline h-3.5 w-3.5 mr-1 -mt-0.5" />
                {interpretation}
              </div>
            )}
          </div>
        )}

        <div className="pt-2 border-t border-amber-200">
          <div className="text-xs font-medium text-amber-900 mb-2 flex items-center gap-1">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Manual unblock — flips email_verified=true for the user above
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              data-testid="admin-manual-verify-reason"
              placeholder="Audit reason (required) — e.g. 'beta-launch unblock'"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="bg-white"
            />
            <Button
              data-testid="admin-manual-verify-btn"
              onClick={runManualVerify}
              disabled={verifying}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {verifying ? 'Verifying…' : 'Manually verify'}
            </Button>
          </div>
          <p className="text-[11px] text-amber-800 mt-2">
            Every manual verify is recorded in admin_audit_log with actor + target + timestamp.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default VerifyDiagnoseCard;
